<template>
	<div class="xs-12">
		<!-- Headline -->
		<div class="flex xs-12 xs-between">
			<div class="fs5 lh125 fw7 c1 mb2">{{ $t("checklists") }}</div>
			<j-button class="tertiary" @click="$modal.show('addChecklist')">
				<crm-icon name="add" size="small" color="c2" />
				{{ $t("add_checklist") }}
			</j-button>
		</div>

		<j-card class="p6" style="height: 100vh; overflow-y: scroll;">
			<case-checklist
				class="mb6"
				v-for="(item, i) in fetchedChecklist"
				:key="i"
				:payload="item"
				@updated="updateChecklist"
				@deleted="deleteChecklist"
			/>

			<j-card
				v-if="fetchedChecklist.length === 0"
				class="xs-12 flex xs-center xs-middle p4 fs3 c1 bg5"
			>{{ $t("errors.no_data") }}</j-card>
		</j-card>

		<core-modal name="addChecklist" :headline="$t('add_checklist')">
			<div class="p4 f">
				<!-- Display options -->
				<div v-if="!showSelectTemplate" class="flex xs-column xs-12">
					<j-button @click="showSelectTemplate = true">{{ $t("choose_template") }}</j-button>
					<div class="flex xs-12 xs-center mt1 mb1">
						<span class="fs4 fw7 c6">{{ $t("or") }}</span>
					</div>
					<j-button type="bordered" @click="addCheckListToCase">{{ $t("add_blank") }}</j-button>
				</div>

				<div v-if="showSelectTemplate">
					<j-base-select
						class="mr2"
						v-model="selectedTemplateId"
						:options="computedTemplateOptions"
					/>
					<div class="flex xs-12 xs-right mt4">
						<j-button
							type="secondary"
							@click="showSelectTemplate = false"
							class="mr2"
						>{{ $t("back") }}</j-button>
						<j-button @click="addCheckListToCase">{{ $t("add") }}</j-button>
					</div>
				</div>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import CrmIcon from "@/components/CrmIcon/"
import CaseChecklist from "./CaseChecklist/"
import CoreModal from "@/modules/core/components/CoreModal"
import JBaseSelect from "@/components/JBaseSelect/"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JButton,
		JCard,
		CrmIcon,
		CaseChecklist,
		CoreModal,
		JBaseSelect
	},

	data() {
		return {
			showSelectTemplate: false,
			selectedTemplateId: null,
			fetchedTemplates: [],
			fetchedChecklist: []
		}
	},

	methods: {
		// fetch list of checklist templates from dynamicTemplates
		async fetchChecklistTemplates() {
			const query = gql`
				query fetchChecklistTemplates($where: templateWhereInput!) {
					templates(where: $where) {
						list {
							id
							name
							description
							... on dynamicTemplate {
								dynamicValues {
									valueId
									valueJsonString
								}
							}
						}
					}
				}
			`
			const variables = {
				where: {
					type: "dynamic",
					id: "a7ced3e9-cb60-4216-bdfe-6b11d0ef8d96" // hardcoded dynamicTemplate that will contain all checklists
				}
			}
			const res = await api({ query, variables })
			this.fetchedTemplates = res?.data?.templates?.list?.[0]?.dynamicValues?.map(
				item => JSON.parse(item?.valueJsonString)
			)
		},

		// check facts on case for any fact type startsWith "checklist-"
		async fetchChecklistOnCase() {
			const query = gql`
				query fetchChecklistOnCase($where: caseWhere!) {
					cases(where: $where) {
						list {
							facts {
								id
								type
								value {
									arrayValue {
										stringValue
									}
								}
							}
						}
					}
				}
			`
			const variables = {
				where: {
					id: this.$route.params.id
				}
			}
			const res = await api({ query, variables })
			this.fetchedChecklist = res?.data?.cases?.list?.[0]?.facts?.filter(item =>
				item?.type?.startsWith("checklist-")
			)
		},

		// adds checklist template as a fact in case
		async addCheckListToCase() {
			const query = gql`
				mutation addCheckListToCase($caseId: String!, $input: addFactInput!) {
					addFactToCase(caseId: $caseId, input: $input) {
						status
						errors
					}
				}
			`
			const selectedTemplate = this.fetchedTemplates?.find(
				item => item.id === this.selectedTemplateId
			)
			const variables = {
				caseId: this.$route.params.id,
				input: {
					type: selectedTemplate
						? `checklist-${selectedTemplate?.name}`
						: `checklist-New Checklist`,
					value: {
						arrayValue: selectedTemplate
							? selectedTemplate?.list?.map(item => ({
									stringValue: JSON.stringify(item)
							  }))
							: [] // handle adding empty checklist
					}
				}
			}
			const res = await api({ query, variables })
			if (res?.data?.addFactToCase?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.addFactToCase?.errors ||
							res?.data?.addFactToCase?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("checklist_added")
					}
				})
			this.fetchChecklistOnCase()
			this.$modal.hide("addChecklist")
		},

		// checklist state is saved in facts
		// update fact
		async updateChecklist({ id, list, name }) {
			const query = gql`
				mutation updateChecklist(
					$caseId: String!
					$factId: String!
					$input: updateFactInput!
				) {
					updateFactOfCase(caseId: $caseId, factId: $factId, input: $input) {
						status
						errors
					}
				}
			`
			const variables = {
				caseId: this.$route.params.id,
				factId: id,
				input: {
					type: `checklist-${name}`,
					value: {
						arrayValue: list?.map(item => ({
							stringValue: JSON.stringify(item)
						}))
					}
				}
			}

			const res = await api({ query, variables })
			if (res?.data?.updateFactOfCase?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.updateFactOfCase?.errors ||
							res?.data?.updateFactOfCase?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			}
		},

		// remove fact on case/ remove checklist on case
		async deleteChecklist(id) {
			const query = gql`
				mutation deleteChecklist($caseId: String!, $factId: String!) {
					removeFactFromCase(caseId: $caseId, factId: $factId) {
						status
						errors
					}
				}
			`
			const variables = {
				caseId: this.$route.params.id,
				factId: id
			}
			const res = await api({ query, variables })
			if (res?.data?.removeFactFromCase?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.removeFactFromCase?.errors ||
							res?.data?.removeFactFromCase?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("checklist_deleted")
					}
				})
			this.fetchChecklistOnCase()
		}
	},

	computed: {
		computedTemplateOptions() {
			return this.fetchedTemplates?.map(item => ({
				name: item?.name,
				value: item?.id
			}))
		}
	},

	created() {
		this.fetchChecklistTemplates()
		this.fetchChecklistOnCase()
	}
}
</script>